import { useContext, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { SanityAccountAddressDetailsStrings } from '@data/sanity/queries/types/strings'
import { useDeleteAddress } from '@lib/shopify/graphql/customer-address'
import { fadeAnimation } from '@lib/animate'
import {
  defaultCountryCodes,
  englishCountryNames,
  LanguageContext,
} from '@lib/language'
import { StringsContext } from '@lib/strings-context'
import { UserContext } from '@lib/user/context'
import { AddressFormValues } from '@lib/user/types'

import Button from '@components/buttons/button'
import AddressForm, { AddressFormMode } from './address-form'

interface AccountAddressDetailsProps {
  accountStrings: SanityAccountAddressDetailsStrings
  className?: string
}

const AccountAddressDetails = ({
  accountStrings,
  className,
}: AccountAddressDetailsProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)
  const { user } = useContext(UserContext)

  const [formMode, setFormMode] = useState<AddressFormMode>(
    AddressFormMode.CREATE,
  )
  const [addressId, setAddressId] = useState('')
  const [showForm, setShowForm] = useState(false)

  const initialValues: AddressFormValues = {
    firstName: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    country: englishCountryNames[defaultCountryCodes[locale]],
    zip: '',
    phone: '',
    isDefault: false,
  }
  const [formDefaultValues, setFormDefaultValues] =
    useState<AddressFormValues>(initialValues)

  const deleteAddress = useDeleteAddress()

  const token = user?.token
  const addresses = user?.addresses ?? []

  if (!token) {
    return null
  }

  const showCreateForm = () => {
    setFormMode(AddressFormMode.CREATE)
    setFormDefaultValues(initialValues)
    setAddressId('')
    setShowForm(true)
  }

  const showEditForm = (id: string, values: AddressFormValues) => {
    setFormMode(AddressFormMode.EDIT)
    setFormDefaultValues(values)
    setAddressId(id)
    setShowForm(true)
  }

  return (
    <div className={className}>
      <AnimatePresence mode="wait">
        {showForm && (
          <motion.div
            key="form"
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
          >
            <AddressForm
              accountStrings={accountStrings}
              mode={formMode}
              hide={() => setShowForm(false)}
              addressId={addressId}
              defaultValues={formDefaultValues}
            />
          </motion.div>
        )}

        {!showForm && (
          <motion.div
            key="list"
            initial="hide"
            animate="show"
            exit="hide"
            variants={fadeAnimation}
          >
            <div className="p-6 lg:p-8 mb-7 max-w-[400px] mx-auto bg-gray-light bg-opacity-50">
              <h4 className="font-semibold">
                {accountStrings.accountAddAddressHeading}
              </h4>

              <Button
                variant="secondary"
                size="small"
                onClick={showCreateForm}
                className="min-w-[150px] mt-5"
              >
                {accountStrings.accountAddAddress}
              </Button>
            </div>

            {addresses.map((address) => (
              <div
                key={address.id}
                className="p-6 lg:p-8 mb-7 max-w-[400px] mx-auto bg-gray-light bg-opacity-50"
              >
                {address.isDefault && (
                  <h4 className="mb-5 font-semibold">
                    {accountStrings.accountDefaultAddressLabel}
                  </h4>
                )}

                <div>
                  {address.formatted.map((addressLine) => (
                    <div key={addressLine} className="text-sm leading-normal">
                      {addressLine}
                    </div>
                  ))}
                </div>

                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => showEditForm(address.id, address.values)}
                  className="min-w-[120px] mt-5"
                >
                  {strings.buttonEdit}
                </Button>
                <button
                  className="mt-5 ml-7 font-semibold text-sm text-red"
                  onClick={() => deleteAddress(address.id, token)}
                >
                  {strings.buttonDelete}
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AccountAddressDetails
